import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { withdrawFunds } from "@services/krent.service";
import styles from "./HostWallet.module.css";

interface Props {
  walletId: string;
  available: number;
  hasPin: boolean;
  hasBankAccount: boolean;
  onClose: () => void;
  onRequested: () => void;
}

const naira = (value: number) => `₦${value.toLocaleString("en-NG")}`;

export default function WithdrawModal({
  walletId,
  available,
  hasPin,
  hasBankAccount,
  onClose,
  onRequested,
}: Props) {
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [requested, setRequested] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const value = Number(amount);
    if (!value || value <= 0) return setError("Enter an amount to withdraw.");
    if (value > available) return setError(`You can withdraw up to ${naira(available)}.`);
    if (!/^\d{4}$/.test(pin)) return setError("Enter your four-digit PIN.");

    setSubmitting(true);
    setError("");
    try {
      const res: any = await withdrawFunds(
        { id: walletId, data: { amount: value, pin } },
        new AbortController().signal,
      );
      if (!res?.success) {
        throw new Error(res?.errors?.[0]?.message || res?.message || "Unable to request a withdrawal.");
      }
      setRequested(true);
      onRequested();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to request a withdrawal.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal show centered onHide={() => !submitting && onClose()} backdrop={submitting ? "static" : true}>
      <Modal.Body className={styles["modalBody"]}>
        <h2 className={styles["modalTitle"]}>Withdraw to your bank</h2>
        {requested ? (
          <>
            <p className={styles["modalText"]} role="status">
              Your request for {naira(Number(amount))} was sent to Krent. You&apos;ll be notified once it has been paid
              to your bank account.
            </p>
            <button type="button" className={styles["primaryButton"]} onClick={onClose}>
              Done
            </button>
          </>
        ) : !hasBankAccount ? (
          <p className={styles["modalText"]}>Link a bank account first so Krent knows where to send your money.</p>
        ) : !hasPin ? (
          <p className={styles["modalText"]}>
            Create a transaction PIN in <Link to="/settings?tab=pin">Settings</Link> before requesting a withdrawal.
          </p>
        ) : (
          <form onSubmit={submit}>
            <p className={styles["modalText"]}>Available to withdraw: {naira(available)}</p>
            <label className={styles["fieldLabel"]} htmlFor="withdraw-amount">
              Amount
            </label>
            <div className={styles["amountRow"]}>
              <input
                id="withdraw-amount"
                className={styles["input"]}
                type="number"
                inputMode="decimal"
                min={1}
                max={available}
                value={amount}
                disabled={submitting}
                onChange={(event) => {
                  setAmount(event.target.value);
                  setError("");
                }}
              />
              <button type="button" className={styles["linkButton"]} disabled={submitting} onClick={() => setAmount(String(available))}>
                Withdraw all
              </button>
            </div>
            <label className={styles["fieldLabel"]} htmlFor="withdraw-pin">
              Transaction PIN
            </label>
            <input
              id="withdraw-pin"
              className={styles["input"]}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              value={pin}
              disabled={submitting}
              onChange={(event) => {
                setPin(event.target.value.replace(/\D/g, "").slice(0, 4));
                setError("");
              }}
            />
            {error && (
              <p className={styles["errorText"]} role="alert">
                {error}
              </p>
            )}
            <button className={styles["primaryButton"]} type="submit" disabled={submitting}>
              {submitting ? "Sending request…" : "Request withdrawal"}
            </button>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
}
